// Port of TrailNavigation/RouteTrack.swift.

import type { GeoBounds, GeoCoordinate, GeoPolyline, RoutePoint, TrailRoute } from '../core/models';
import {
  bearing,
  coordinateAtDistance,
  distance,
  isValidCoordinate,
  metersPerDegreeLatitude,
  metersPerDegreeLongitude,
  projectOntoPolyline,
  projectOntoSegment,
  type PolylineProjection,
} from '../core/geo';
import { defaultElevationThreshold } from '../core/elevation';
import { NavigationLog } from './navigationLog';

/** A run of consecutive segments `[segmentStart, segmentEnd)` with their bounding box (coarse spatial index). */
export interface SegmentChunk {
  segmentStart: number;
  segmentEnd: number;
  bounds: GeoBounds;
}

/**
 * Precomputed route geometry for fast repeated queries.
 *
 * A `RouteTrack` flattens a `TrailRoute` into parallel arrays once (coordinates, cumulative distance, cumulative
 * ascent/descent and gap-filled elevations) so that the work done on every GPS fix during a 12-hour day stays cheap:
 * - coordinate, elevation and ascent/descent lookups by distance are `O(log n)` binary searches;
 * - projection with a distance window is `O(window)`;
 * - a full projection walks a coarse bounding-box index (chunks of 32 segments) nearest-first and stops as soon as no
 *   remaining chunk can beat the best match.
 *
 * Vertices with invalid coordinates are dropped and non-finite elevations are treated as missing, so malformed
 * imported data can never produce `NaN` distances. Instances are immutable.
 */
export class RouteTrack {
  /** Windowed projections farther than this from the route trigger a full search. */
  static readonly windowFallbackDistance = 200;
  /** Number of segments per bounding-box chunk. */
  static readonly chunkSize = 32;
  /**
   * Minimum cross-track tolerance (metres) within which two passes of the route are considered equally plausible for
   * a fix (GPS noise is typically 5–15 m).
   */
  static readonly minimumAmbiguityTolerance = 30;

  /** The route this track was built from (unchanged, including any invalid vertices). */
  readonly route: TrailRoute;
  /** Valid route vertices in travel order. */
  readonly points: readonly RoutePoint[];
  /** Coordinates of `points`. */
  readonly coordinates: readonly GeoCoordinate[];
  /** 2D distance in metres from the route start at each vertex; the first element is 0. */
  readonly cumulativeDistance: readonly number[];
  /**
   * Ascent in metres accumulated up to each vertex, using exactly the hysteresis of `computeElevationStatistics`, so
   * `totalAscent` equals its `ascent` for the same points.
   */
  readonly cumulativeAscent: readonly number[];
  /** Descent in metres accumulated up to each vertex (same hysteresis as `cumulativeAscent`). */
  readonly cumulativeDescent: readonly number[];
  /** Hysteresis threshold in metres used for the cumulative ascent/descent. */
  readonly elevationThreshold: number;
  /**
   * Elevation at every vertex with gaps linearly interpolated along distance (ends are held constant). `undefined`
   * when no vertex carries an elevation. Internal (Swift `internal`).
   */
  readonly filledElevations: readonly number[] | undefined;
  /** Coarse spatial index used by the full projection search. Internal. */
  readonly chunks: readonly SegmentChunk[];

  /** Builds a track from a route. `elevationThreshold` is the ascent/descent hysteresis in metres. */
  constructor(route: TrailRoute, elevationThreshold: number = defaultElevationThreshold) {
    this.route = route;
    const threshold = Number.isFinite(elevationThreshold) ? Math.max(0, elevationThreshold) : defaultElevationThreshold;
    this.elevationThreshold = threshold;

    const valid: RoutePoint[] = [];
    for (const point of route.polyline.points) {
      if (!isValidCoordinate(point.coordinate)) continue;
      if (point.elevation !== undefined && !Number.isFinite(point.elevation)) {
        valid.push({ coordinate: point.coordinate });
      } else {
        valid.push(point);
      }
    }
    const dropped = route.polyline.points.length - valid.length;
    if (dropped > 0) NavigationLog.logger.notice(`RouteTrack dropped ${dropped} invalid vertices`);

    const coordinates = valid.map((p) => p.coordinate);
    const cumulative: number[] = new Array(valid.length);
    const ascent: number[] = new Array(valid.length);
    const descent: number[] = new Array(valid.length);

    let running = 0;
    // Same trend-following hysteresis as computeElevationStatistics so totals match exactly: a direction change
    // counts once it reaches the threshold; a confirmed climb/descent counts every metre.
    let anchor: number | undefined;
    let trend = 0;
    let up = 0;
    let down = 0;
    for (let index = 0; index < valid.length; index++) {
      const point = valid[index];
      if (index > 0) running += distance(coordinates[index - 1], point.coordinate);
      cumulative[index] = running;
      const elevation = point.elevation;
      if (elevation !== undefined && Number.isFinite(elevation)) {
        if (anchor !== undefined) {
          const diff = elevation - anchor;
          if (trend > 0 && diff > 0) {
            up += diff;
            anchor = elevation;
          } else if (trend < 0 && diff < 0) {
            down += -diff;
            anchor = elevation;
          } else if (diff > 0 && diff >= threshold) {
            up += diff;
            anchor = elevation;
            trend = 1;
          } else if (diff < 0 && -diff >= threshold) {
            down += -diff;
            anchor = elevation;
            trend = -1;
          }
        } else {
          anchor = elevation;
        }
      }
      ascent[index] = up;
      descent[index] = down;
    }

    this.points = valid;
    this.coordinates = coordinates;
    this.cumulativeDistance = cumulative;
    this.cumulativeAscent = ascent;
    this.cumulativeDescent = descent;
    this.filledElevations = RouteTrack.fillElevations(valid, cumulative);
    this.chunks = RouteTrack.buildChunks(coordinates);
  }

  /** Builds a track from a bare polyline (e.g. an imported GPX track without stage metadata). */
  static fromPolyline(polyline: GeoPolyline, id = 'track', name = ''): RouteTrack {
    return new RouteTrack({
      id,
      name,
      stageIDs: [],
      isReversed: false,
      polyline,
      stageStartDistances: [],
    });
  }

  /** Total 2D length in metres. */
  get totalDistance(): number {
    return this.cumulativeDistance[this.cumulativeDistance.length - 1] ?? 0;
  }

  /** Total ascent in metres (hysteresis applied). */
  get totalAscent(): number {
    return this.cumulativeAscent[this.cumulativeAscent.length - 1] ?? 0;
  }

  /** Total descent in metres (hysteresis applied). */
  get totalDescent(): number {
    return this.cumulativeDescent[this.cumulativeDescent.length - 1] ?? 0;
  }

  // MARK: Lookups by distance

  /** Coordinate at a distance along the route (clamped to the ends). `undefined` for an empty track. */
  coordinateAtDistance(d: number): GeoCoordinate | undefined {
    if (!Number.isFinite(d)) return undefined;
    return coordinateAtDistance(d, this.coordinates, this.cumulativeDistance);
  }

  /**
   * Elevation at a distance along the route, linearly interpolated between vertices (clamped to the ends).
   * `undefined` when the route carries no elevation data.
   */
  elevationAtDistance(d: number): number | undefined {
    const filled = this.filledElevations;
    if (!Number.isFinite(d) || !filled) return undefined;
    return this.interpolate(filled, d);
  }

  /**
   * Ascent/descent between two distances in travel direction (from < to). If from > to, returns the reverse-travel
   * values.
   *
   * The cumulative hysteresis arrays are interpolated linearly inside segments, so results are additive:
   * `ascentDescent(a, b) + ascentDescent(b, c) == ascentDescent(a, c)` and the full range equals the totals. Walking
   * backwards, the forward descent is what gets climbed, so the reverse-travel ascent is the forward descent over the
   * same interval (and vice versa).
   */
  ascentDescent(from: number, to: number): { ascent: number; descent: number } {
    if (!Number.isFinite(from) || !Number.isFinite(to) || this.cumulativeDistance.length <= 1) {
      return { ascent: 0, descent: 0 };
    }
    const ascentFrom = this.interpolate(this.cumulativeAscent, from);
    const ascentTo = this.interpolate(this.cumulativeAscent, to);
    const descentFrom = this.interpolate(this.cumulativeDescent, from);
    const descentTo = this.interpolate(this.cumulativeDescent, to);
    if (from <= to) {
      return { ascent: Math.max(0, ascentTo - ascentFrom), descent: Math.max(0, descentTo - descentFrom) };
    }
    return { ascent: Math.max(0, descentFrom - descentTo), descent: Math.max(0, ascentFrom - ascentTo) };
  }

  /**
   * Index (into `route.stageIDs` / `route.stageStartDistances`) of the stage containing a distance. Distances before
   * the first stage start map to stage 0, beyond the end to the last stage. `undefined` when the route has no stage
   * layout.
   */
  stageIndexAtDistance(d: number): number | undefined {
    const starts = this.route.stageStartDistances;
    if (starts.length === 0 || Number.isNaN(d)) return undefined;
    // First index whose start is > d; the stage is the one before it.
    let lo = 0;
    let hi = starts.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (starts[mid] <= d) lo = mid + 1;
      else hi = mid;
    }
    return Math.max(0, lo - 1);
  }

  // MARK: Projection

  /**
   * Projection; when `nearDistance` is given, first search ±`window` metres around it (hysteresis for out-and-back
   * routes), falling back to a full search if the windowed result is > 200 m away.
   *
   * Inside the window, every local minimum of the cross-track distance is a distinct "pass" of the route near the fix.
   * When several passes are within the GPS ambiguity tolerance (`max(30 m, 25 % of the best cross-track distance)`)
   * the one closest to `nearDistance` along the route wins, so an out-and-back section or tight switchbacks do not make
   * the position jump. After a full-search fallback the global result replaces the windowed one only when it is
   * closer by more than that tolerance (e.g. the hiker took a bus), again to avoid flapping.
   *
   * `travelCourse` (degrees clockwise from north, e.g. the GPS course or the bearing between two fixes) restricts
   * ambiguous passes to those whose segment direction is within 90° of it, which resolves the turnaround of an
   * out-and-back route even while standing on the overlap.
   */
  project(
    c: GeoCoordinate,
    nearDistance?: number,
    window = 1500,
    travelCourse?: number,
  ): PolylineProjection | undefined {
    if (!isValidCoordinate(c) || this.coordinates.length === 0) return undefined;
    if (this.coordinates.length < 2) return projectOntoPolyline(c, this.coordinates, this.cumulativeDistance);
    if (nearDistance === undefined || !Number.isFinite(nearDistance) || !Number.isFinite(window) || !(window > 0)) {
      return this.fullSearch(c);
    }
    const first = this.segmentIndexContaining(nearDistance - window);
    const last = this.segmentIndexContaining(nearDistance + window);
    const windowed = this.windowedSearch(c, first, last, nearDistance, travelCourse);
    if (!windowed) return this.fullSearch(c);
    if (!(windowed.crossTrackDistance > RouteTrack.windowFallbackDistance)) return windowed;
    const global = this.fullSearch(c);
    if (!global) return windowed;
    const tolerance = RouteTrack.ambiguityTolerance(windowed.crossTrackDistance);
    return global.crossTrackDistance < windowed.crossTrackDistance - tolerance ? global : windowed;
  }

  // MARK: Internals

  /**
   * Index `i` of the segment `i → i+1` that contains distance `d` (clamped to the valid segments). Requires at least
   * two vertices.
   */
  segmentIndexContaining(d: number): number {
    const cumulative = this.cumulativeDistance;
    const lastVertex = cumulative.length - 1;
    if (lastVertex < 1) return 0;
    if (Number.isNaN(d) || d <= cumulative[0]) return 0;
    if (d >= cumulative[lastVertex]) return lastVertex - 1;
    let lo = 0;
    let hi = lastVertex;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (cumulative[mid] <= d) lo = mid;
      else hi = mid;
    }
    return lo;
  }

  /** Linear interpolation of a per-vertex array at a distance (clamped to the ends). */
  interpolate(values: readonly number[], d: number): number {
    if (values.length === 0) return 0;
    const firstValue = values[0];
    const cumulative = this.cumulativeDistance;
    if (values.length <= 1 || values.length !== cumulative.length) return firstValue;
    if (d <= 0) return firstValue;
    if (d >= this.totalDistance) return values[values.length - 1];
    const i = this.segmentIndexContaining(d);
    const length = cumulative[i + 1] - cumulative[i];
    if (!(length > 0)) return values[i];
    const t = Math.min(1, Math.max(0, (d - cumulative[i]) / length));
    return values[i] + (values[i + 1] - values[i]) * t;
  }

  static ambiguityTolerance(crossTrackDistance: number): number {
    return Math.max(RouteTrack.minimumAmbiguityTolerance, 0.25 * crossTrackDistance);
  }

  /** Projection onto a single segment, expressed as a polyline projection. */
  projectionOnSegment(c: GeoCoordinate, index: number): PolylineProjection | undefined {
    return projectOntoPolyline(c, this.coordinates, this.cumulativeDistance, { start: index, end: index + 1 });
  }

  /** Searches segments `first…last` (inclusive) and resolves ambiguity between several passes of the route. */
  windowedSearch(
    c: GeoCoordinate,
    first: number,
    last: number,
    near: number,
    travelCourse: number | undefined,
  ): PolylineProjection | undefined {
    const count = last - first + 1;
    if (count <= 0) return undefined;
    const coordinates = this.coordinates;
    const cumulative = this.cumulativeDistance;
    const distances = new Array<number>(count);
    const alongs = new Array<number>(count);
    let bestOffset = 0;
    for (let offset = 0; offset < count; offset++) {
      const i = first + offset;
      const p = projectOntoSegment(c, coordinates[i], coordinates[i + 1]);
      const length = cumulative[i + 1] - cumulative[i];
      distances[offset] = p.distance;
      alongs[offset] = cumulative[i] + length * p.fraction;
      if (p.distance < distances[bestOffset]) bestOffset = offset;
    }

    const limit = distances[bestOffset] + RouteTrack.ambiguityTolerance(distances[bestOffset]);
    // Local minima of the cross-track profile are distinct passes. `<=` on the left and `<` on the right keeps a
    // single representative when two segments clamp to their shared vertex.
    const passes: number[] = [];
    for (let offset = 0; offset < count; offset++) {
      if (!(distances[offset] <= limit)) continue;
      const leftOK = offset === 0 || distances[offset] <= distances[offset - 1];
      const rightOK = offset === count - 1 || distances[offset] < distances[offset + 1];
      if (leftOK && rightOK) passes.push(offset);
    }

    let chosen = bestOffset;
    if (passes.length > 1) {
      let pool = passes;
      if (travelCourse !== undefined && Number.isFinite(travelCourse)) {
        const aligned = passes.filter((offset) => {
          const i = first + offset;
          // Degenerate segments carry no direction: keep them as candidates.
          if (!(cumulative[i + 1] - cumulative[i] > 0.5)) return true;
          const segmentBearing = bearing(coordinates[i], coordinates[i + 1]);
          return RouteTrack.angularDifference(segmentBearing, travelCourse) <= 90;
        });
        if (aligned.length > 0) pool = aligned;
      }
      // Continuity: the pass closest (along the route) to the previous position wins.
      let best = pool[0];
      for (const candidate of pool.slice(1)) {
        const da = Math.abs(alongs[candidate] - near);
        const db = Math.abs(alongs[best] - near);
        if (da < db || (da === db && distances[candidate] < distances[best])) best = candidate;
      }
      chosen = best;
    }
    return this.projectionOnSegment(c, first + chosen);
  }

  /**
   * Nearest projection over the whole route. Ties resolve to the lowest segment index (the earliest pass), matching a
   * brute-force scan.
   */
  fullSearch(c: GeoCoordinate): PolylineProjection | undefined {
    const order = this.chunks.map((chunk, index) => ({ bound: RouteTrack.lowerBoundDistance(c, chunk.bounds), index }));
    order.sort((a, b) => a.bound - b.bound || a.index - b.index);
    let best: PolylineProjection | undefined;
    for (const entry of order) {
      if (best && entry.bound > best.crossTrackDistance) break;
      const chunk = this.chunks[entry.index];
      const candidate = projectOntoPolyline(c, this.coordinates, this.cumulativeDistance, {
        start: chunk.segmentStart,
        end: chunk.segmentEnd,
      });
      if (!candidate) continue;
      if (
        !best ||
        candidate.crossTrackDistance < best.crossTrackDistance ||
        (candidate.crossTrackDistance === best.crossTrackDistance && candidate.segmentIndex < best.segmentIndex)
      ) {
        best = candidate;
      }
    }
    return best;
  }

  /** Absolute difference between two bearings in degrees, 0…180. */
  static angularDifference(a: number, b: number): number {
    const diff = (a - b) % 360;
    const wrapped = diff < 0 ? diff + 360 : diff;
    return wrapped > 180 ? 360 - wrapped : wrapped;
  }

  /**
   * Conservative lower bound (metres) of the distance from a point to anything inside `bounds`. Uses the smallest
   * longitude scale over the involved latitudes and a 5 % safety margin, so a chunk is never pruned when it could
   * contain a closer point.
   */
  static lowerBoundDistance(c: GeoCoordinate, bounds: GeoBounds): number {
    const dLat = Math.max(0, bounds.minLatitude - c.latitude, c.latitude - bounds.maxLatitude);
    const dLon = Math.max(0, bounds.minLongitude - c.longitude, c.longitude - bounds.maxLongitude);
    if (dLat === 0 && dLon === 0) return 0;
    const highestLatitude = Math.min(
      89.9,
      Math.max(Math.abs(bounds.minLatitude), Math.abs(bounds.maxLatitude), Math.abs(c.latitude)),
    );
    const dy = dLat * metersPerDegreeLatitude;
    const dx = dLon * metersPerDegreeLongitude(highestLatitude);
    return Math.sqrt(dx * dx + dy * dy) * 0.95;
  }

  static buildChunks(coordinates: readonly GeoCoordinate[]): SegmentChunk[] {
    const segmentCount = coordinates.length - 1;
    if (segmentCount < 1) return [];
    const chunks: SegmentChunk[] = [];
    let start = 0;
    while (start < segmentCount) {
      const end = Math.min(start + RouteTrack.chunkSize, segmentCount);
      // Segments start..<end use vertices start...end.
      let minLatitude = Infinity;
      let minLongitude = Infinity;
      let maxLatitude = -Infinity;
      let maxLongitude = -Infinity;
      for (let i = start; i <= end; i++) {
        const c = coordinates[i];
        if (c.latitude < minLatitude) minLatitude = c.latitude;
        if (c.latitude > maxLatitude) maxLatitude = c.latitude;
        if (c.longitude < minLongitude) minLongitude = c.longitude;
        if (c.longitude > maxLongitude) maxLongitude = c.longitude;
      }
      const bounds: GeoBounds = { minLatitude, minLongitude, maxLatitude, maxLongitude };
      chunks.push({ segmentStart: start, segmentEnd: end, bounds });
      start = end;
    }
    return chunks;
  }

  static fillElevations(points: readonly RoutePoint[], cumulative: readonly number[]): number[] | undefined {
    const filled = new Array<number>(points.length).fill(0);
    let previousKnown: number | undefined;
    for (let index = 0; index < points.length; index++) {
      const elevation = points[index].elevation;
      if (elevation === undefined) continue;
      filled[index] = elevation;
      if (previousKnown !== undefined) {
        if (index - previousKnown > 1) {
          const start = filled[previousKnown];
          const span = cumulative[index] - cumulative[previousKnown];
          for (let gap = previousKnown + 1; gap < index; gap++) {
            const t = span > 0 ? (cumulative[gap] - cumulative[previousKnown]) / span : 0;
            filled[gap] = start + (elevation - start) * t;
          }
        }
      } else {
        for (let gap = 0; gap < index; gap++) filled[gap] = elevation;
      }
      previousKnown = index;
    }
    if (previousKnown === undefined) return undefined;
    for (let gap = previousKnown + 1; gap < points.length; gap++) filled[gap] = filled[previousKnown];
    return filled;
  }
}
